"use client";

import React, { useEffect, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useDictionary } from "../DictProvider/DictProvider";
import {
  TextField,
  Autocomplete,
  CircularProgress,
  Button,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import usePost from "@/hooks/usePost";
import Loading from "@/app/[lang]/loading";
import ClearIcon from "@mui/icons-material/Clear";

const SearchComponent = () => {
  const pathname = usePathname();
  const router = useRouter();
  const dict = useDictionary();
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const { PostMethod } = usePost();

  // State for debouncing
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  const fetchSuggestions = async (input: string) => {
    if (input.length === 0) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    try {
      const response = await PostMethod({
        method: "get",
        url: `product/searchName/${input}`,
        language:pathname.split("/")[1]
      });

      if (response?.success) {
        const suggestionsArray = response.data as string[];
        const uniqueSuggestions = Array.from(new Set(suggestionsArray));
        setSuggestions(uniqueSuggestions);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    setQuery(inputValue);

    // Clear the previous timeout if it exists
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    // Set a new timeout
    const timeout = setTimeout(() => {
      fetchSuggestions(inputValue);
    }, 300); // Adjust the delay as necessary

    setDebounceTimeout(timeout);
  };

  const handleSearchSubmit = (
    e?: React.FormEvent<HTMLFormElement>,
    val?: string
  ) => {
    e?.preventDefault(); // Prevent default form submission if an event is passed
    const searchQuery = val || query;

    if (
      searchQuery &&
      searchQuery?.toLocaleLowerCase() !==
        decodeURIComponent(pathname.split("/")[3]?.toLocaleLowerCase())
    ) {
      const locale = pathname.split("/")[1] || "en"; // Default to 'en'
      setPageLoading(true);
      router.push(`/${locale}/search/${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionSelect = (
    event: React.SyntheticEvent,
    value: string | null
  ) => {
    if (value) {
      handleSearchSubmit(undefined, value);
    }
  };

  useEffect(() => {
    if (pathname.split("/")[2] === "search") {
      setPageLoading(false);
    } else {
      resetSearchState();
    }
  }, [pathname]);

  const resetSearchState = () => {
    setQuery("");
    setSuggestions([]);
  };

  return (
    <div>
      {pageLoading && (
        <div className="loading-bar absolute inset-0 w-screen h-screen overflow-hidden z-[99999] bg-white/50">
          <Loading />
        </div>
      )}

      <form onSubmit={handleSearchSubmit} className="flex w-full items-center">
        <Autocomplete
          freeSolo
          inputValue={query}
          onInputChange={(event, newValue) => setQuery(newValue)}
          onChange={handleSuggestionSelect}
          options={suggestions}
          loading={loading}
          className="!outline flex justify-start items-center !outline-2 focus:!outline-black !outline-secondary !rounded-md w-full h-[40px] shadow-sm placeholder:text-gray-400 leading-8 md:leading-6"
          renderInput={(params) => (
            <TextField
              {...params}
              onChange={handleInputChange}
              fullWidth
              variant="outlined"
              sx={{
                "& .MuiOutlinedInput-root": {
                  maxHeight: "40px",
                  borderRadius: "8px",
                  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.1)",
                  "& fieldset": {
                    border: "none",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  fontSize: "1rem !important", // Adjust this as needed
                },
                "& .MuiInputBase-input": {
                  fontSize: "1rem !important", // Adjust this as needed
                  padding: "0 !important",
                },
              }}
              placeholder={dict.productListPage.searchQueryPlaceHolderNEW}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading && <CircularProgress color="inherit" size={20} />}
                    {query && (
                      <InputAdornment position="end">
                        <ClearIcon
                          style={{ cursor: "pointer" }}
                          onClick={resetSearchState}
                        />
                      </InputAdornment>
                    )}
                  </>
                ),
              }}
            />
          )}
        />
        <Button
          type="submit"
          variant="contained"
          sx={{
            minWidth: "100px",
            minHeight: "40px",
            backgroundColor: "#AC7529",
            color: "white",
            "&:hover": {
              backgroundColor: "#8E6022",
            },
            marginLeft: pathname.split("/")[1] === "en" ? "10px" : 0,
            marginRight: pathname.split("/")[1] === "ar" ? "10px" : 0,
          }}
        >
          {dict?.productListPage?.search}
        </Button>
      </form>
    </div>
  );
};

export default SearchComponent;
